// Type-only import: the SDK itself is loaded lazily in initPostHog so it stays out of the entry
// bundle (it is ~110 kB gzip, more than the rest of the entry chunk combined).
import type { CaptureResult, PostHog } from "posthog-js";

const KEY = (import.meta.env.VITE_POSTHOG_KEY as string | undefined)?.trim();
const HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined)?.trim() || "https://eu.i.posthog.com";

// Production builds only, and only on the real hosts. Vercel preview deploys are production
// builds too, so the hostname check keeps their traffic out of the numbers.
const PROD_HOSTS = ["scoreglass.app", "www.scoreglass.app"];

/** The one rule for every tracker (PostHog, GA, Clarity): real site, real build. Local dev,
 *  e2e and screenshot browsers each count as a new visitor and once made up half of /login in GA. */
export const isProductionSite = (): boolean =>
  import.meta.env.PROD && PROD_HOSTS.includes(window.location.hostname);
const IGNORE_KEY = "ph_ignore";

/** Pages that show player names, phone numbers or credentials. No session replay here, and
 *  autocaptured element text is stripped so a clicked name or number never leaves the browser. */
export const SENSITIVE_PREFIXES = [
  "/admin",
  "/interleague/admin",
  "/interleague/internal",
  "/communities/manage",
  "/captain",
  "/profile",
  "/login",
  "/auth",
  "/claim",
  "/welcome",
  "/invite",
];

/** Query params that carry a credential or an email. Everything else (?tab=, ?week=, ...) is kept. */
const SENSITIVE_PARAMS = [
  "token",
  "token_hash",
  "invite",
  "email",
  "code",
  "access_token",
  "refresh_token",
  "id_token",
  "error_description",
];

/** Path segments that are bearer-style identifiers, replaced with a placeholder. */
const SENSITIVE_PATHS: Array<[RegExp, string]> = [
  [/^\/invite\/[^/]+/, "/invite/:token"],
  [/^\/captain\/[^/]+/, "/captain/:id"],
];

// Ladder registration shows a signed-in registrant's WhatsApp, email and invite links (bearer tokens).
const SENSITIVE_PATTERNS = [/^\/ladders\/[^/]+\/register\/?$/];

export const isSensitivePath = (pathname: string): boolean =>
  SENSITIVE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`)) ||
  SENSITIVE_PATTERNS.some((re) => re.test(pathname));

/** Drops the hash (Supabase can put auth tokens there), redacts credential params and swaps
 *  bearer-style path segments. Keeps the rest of the query string so tabs stay analyzable. */
export function scrubUrl(raw: string): string {
  try {
    const url = new URL(raw, "https://placeholder.invalid");
    const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(raw);
    url.hash = "";
    for (const key of SENSITIVE_PARAMS) {
      if (url.searchParams.has(key)) url.searchParams.set(key, "redacted");
    }
    for (const [pattern, replacement] of SENSITIVE_PATHS) {
      url.pathname = url.pathname.replace(pattern, replacement);
    }
    return isAbsolute ? url.toString() : `${url.pathname}${url.search}`;
  } catch {
    return raw;
  }
}

/** PostHog's autocapture stores clicked-element text twice: `$el_text` per element and
 *  `text="..."` inside the `$elements_chain` string. Remove both. */
export function stripElementText(props: Record<string, unknown>): void {
  if (Array.isArray(props.$elements)) {
    for (const el of props.$elements as Array<Record<string, unknown>>) delete el.$el_text;
  }
  if (typeof props.$elements_chain === "string") {
    props.$elements_chain = props.$elements_chain.replace(/text="(?:[^"\\]|\\.)*"/g, 'text=""');
  }
  delete props.$el_text;
}

/** Clicked links are captured with their href. A wa.me link can carry a phone number and a
 *  pre-filled message (names), a tel: link is a number. Keep that the link existed, not what it held. */
const CONTACT_LINK = /(?:https?:\/\/)?wa\.me\/[^"\s]*|tel:[^"\s]*/g;

export function redactContactLinks(props: Record<string, unknown>): void {
  const redact = (s: string) => s.replace(CONTACT_LINK, (m) => (m.startsWith("tel:") ? "tel:redacted" : "wa.me/redacted"));
  if (Array.isArray(props.$elements)) {
    for (const el of props.$elements as Array<Record<string, unknown>>) {
      if (typeof el.attr__href === "string") el.attr__href = redact(el.attr__href);
    }
  }
  if (typeof props.$elements_chain === "string") props.$elements_chain = redact(props.$elements_chain);
}

const URL_PROPS = ["$current_url", "$referrer", "$initial_current_url", "$initial_referrer"];

export function sanitizeEvent(event: CaptureResult, pathname: string): CaptureResult {
  const props = event.properties as Record<string, unknown> | undefined;
  if (!props) return event;
  for (const key of URL_PROPS) {
    if (typeof props[key] === "string") props[key] = scrubUrl(props[key] as string);
  }
  redactContactLinks(props);
  if (isSensitivePath(pathname)) stripElementText(props);
  return event;
}

// The SDK arrives after the page is idle. Calls made before that (the first route, identify, early
// events) wait in `pending` and replay in order once it is ready. When PostHog is off (dev, preview,
// ignored browser, no key) nothing queues and every call is a no-op.
let client: PostHog | null = null;
let loading = false;
const pending: Array<(ph: PostHog) => void> = [];
const MAX_PENDING = 100;

function run(fn: (ph: PostHog) => void): void {
  if (client) fn(client);
  else if (loading && pending.length < MAX_PENDING) pending.push(fn);
}

const whenIdle = (fn: () => void): void => {
  if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(fn, { timeout: 4000 });
  else setTimeout(fn, 2000);
};

/** Own devices: visit any page with ?ph_ignore=1 once to stop sending, ?ph_ignore=0 to resume. */
function applyIgnoreFlag(): boolean {
  const flag = new URLSearchParams(window.location.search).get(IGNORE_KEY);
  try {
    if (flag === "0") localStorage.removeItem(IGNORE_KEY);
    // Storage can be blocked (private mode); the flag still applies to this page load.
    if (flag === "1") {
      localStorage.setItem(IGNORE_KEY, "1");
      return true;
    }
    return localStorage.getItem(IGNORE_KEY) === "1";
  } catch {
    return flag === "1";
  }
}

export function initPostHog(): void {
  if (!KEY || !isProductionSite()) return;
  if (applyIgnoreFlag()) return;

  const key = KEY;
  loading = true;
  whenIdle(() => {
    import("posthog-js")
      .then(({ default: posthog }) => {
        posthog.init(key, {
          api_host: HOST,
          // Anonymous visitors don't get a person profile (cheaper, and less personal data stored).
          person_profiles: "identified_only",
          capture_pageview: "history_change",
          capture_pageleave: true,
          // Replay starts per route from onRouteChange so sensitive pages never record, not even
          // the first frames of a direct landing.
          disable_session_recording: true,
          session_recording: {
            maskAllInputs: true,
            // Every WhatsApp link: a number in the path, or names / invite tokens in ?text=.
            // Anything else showing a number gets `ph-no-capture`.
            blockSelector: 'a[href*="wa.me/"], a[href^="tel:"]',
          },
          before_send: (event) => (event ? sanitizeEvent(event, window.location.pathname) : event),
        });
        client = posthog;
        pending.splice(0).forEach((fn) => fn(posthog));
      })
      .catch(() => {
        // Blocked by an ad blocker or a failed chunk load: analytics is optional, the app is not.
        loading = false;
        pending.length = 0;
      });
  });
}

/** Per-route policy: replay on or off. Called from RouteTracker. */
export function onRouteChange(pathname: string): void {
  run((ph) => {
    if (isSensitivePath(pathname)) ph.stopSessionRecording();
    else if (!ph.sessionRecordingStarted()) ph.startSessionRecording();
  });
}

/** Internal user id only: never name, email or phone. */
export function identifyUser(
  userId: string,
  props: { user_type: "player" | "signed_in"; is_super_admin: boolean },
): void {
  run((ph) => ph.identify(userId, props));
}

export function resetAnalyticsUser(): void {
  run((ph) => ph.reset());
}

/** Keys never forwarded to PostHog. GA still receives them today; PostHog should not hold names. */
const DROP_PROPS = new Set(["player_name"]);

export function captureEvent(
  event: string,
  params?: Record<string, string | number | boolean>,
): void {
  const safe: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params ?? {})) if (!DROP_PROPS.has(k)) safe[k] = v;
  run((ph) => ph.capture(event, safe));
}
